import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgFor } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DialogModule } from 'primeng/dialog';

import {
  FILING_FORM_ROUTE,
  FIELD_LINK_SCROLL_PARAM,
} from '../../../utils/field-link-navigation.util';
import type { InboundFieldLink } from '../../../utils/field-link-reverse-index.util';

/**
 * Lets the user pick which source field a BACK-link should return to, when a
 * destination row is the target of more than one forward link.
 *
 * ## Why this exists at all
 *
 * Inverting the shipped `ui.linkTo` declarations yields 58 destination edges, and
 * three have multiple inbound links. One is benign (the MFRS/MPERS variants of
 * the same row, resolved by taxonomy filtering). The other two are genuine: SOCI's
 * "Profit (loss)" is the target of BOTH SOPL's "Profit (loss)" and its "Total
 * Profit (loss)" — two rows on the same sheet legitimately mirroring one row —
 * so there is no single correct back-link target and the user must choose.
 *
 * Developer Note: this dialog's presentation — its copy, layout, ordering, and
 * indeed whether a dialog is the right affordance at all — is PROVISIONAL and
 * owned by the P.O. / UI designer. It was specified only so the two-way
 * capability has a defined behaviour for ambiguous targets; no design review has
 * happened. The component is currently UNREACHABLE, because no schema sets
 * `ui.linkTo.bidirectional`. Expect it to change before it ever renders.
 */
@Component({
  selector: 'app-field-link-back-chooser',
  standalone: true,
  imports: [NgFor, RouterLink, DialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [visible]="visible"
      (visibleChange)="visibleChange.emit($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '32rem' }"
      header="Open which linked field?"
    >
      <p class="chooser-intro">This value is shared by more than one field. Choose where to go:</p>
      <ul class="chooser-options">
        <li *ngFor="let option of options">
          <a
            class="clickable-description"
            [routerLink]="FILING_FORM_ROUTE"
            [queryParams]="queryParamsFor(option)"
            queryParamsHandling="merge"
            (click)="choose(option)"
            >{{ optionLabel(option) }}</a
          >
        </li>
      </ul>
    </p-dialog>
  `,
  styles: [
    `
      .chooser-intro { margin: 0 0 0.75rem; }
      .chooser-options { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; }
    `,
  ],
})
export class FieldLinkBackChooserComponent {
  @Input() options: InboundFieldLink[] = [];
  @Input() visible = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() chosen = new EventEmitter<InboundFieldLink>();

  protected readonly FILING_FORM_ROUTE = [...FILING_FORM_ROUTE];

  /**
   * Field label AND sheet name. The sheet name is not decoration: the known
   * collision has both candidates on the SAME sheet, so the label alone would
   * still be ambiguous — and a future cross-sheet collision would be unreadable
   * without it.
   */
  optionLabel(option: InboundFieldLink): string {
    return `${option.sourceLabel} — ${option.sourceSheetName}`;
  }

  /** Navigate to the source sheet AND scroll to the specific source row. */
  queryParamsFor(option: InboundFieldLink): Record<string, string> {
    return {
      sheet: option.sourceSheet,
      [FIELD_LINK_SCROLL_PARAM]: option.sourceQname,
    };
  }

  choose(option: InboundFieldLink): void {
    this.chosen.emit(option);
    this.visibleChange.emit(false);
  }
}
